import type { ReduxCompany } from 'src/types/apiType'

export const BANK_PROFILE_IDS = [
	'bankProfile1',
	'bankProfile2',
	'bankProfile3',
	'bankProfile4',
	'bankProfile5',
] as const

export type BankProfileId = (typeof BANK_PROFILE_IDS)[number]
export type BankProfileSelection = BankProfileId | 'empty'

export type BankAccountProfile = {
	profileId: BankProfileSelection
	index: number
	bankProfileName: string
	bank: string
	iban: string
	bic: string
	accountName: string
}

type CompanyBankProfiles = Partial<ReduxCompany> & {
	bankProfileName4?: string | null
	bank4?: string | null
	iban4?: string | null
	bic4?: string | null
	accountName4?: string | null
	bankProfileName5?: string | null
	bank5?: string | null
	iban5?: string | null
	bic5?: string | null
	accountName5?: string | null
}

const normalizeValue = (value: unknown) =>
	typeof value === 'string' ? value.trim() : ''

const isBankProfileId = (
	profileId: BankProfileSelection | string | null | undefined
): profileId is BankProfileId => {
	return BANK_PROFILE_IDS.includes(profileId as BankProfileId)
}

const getProfileIndex = (profileId: BankProfileId) =>
	BANK_PROFILE_IDS.indexOf(profileId) + 1

export function getBankAccountProfile(
	company: CompanyBankProfiles | null | undefined,
	profileId: BankProfileSelection | string | null | undefined
): BankAccountProfile {
	if (!company || !isBankProfileId(profileId)) {
		return {
			profileId: 'empty',
			index: 0,
			bankProfileName: '',
			bank: '',
			iban: '',
			bic: '',
			accountName: '',
		}
	}

	const index = getProfileIndex(profileId)
	const suffix = index === 1 ? '' : `${index}`

	return {
		profileId,
		index,
		bankProfileName: normalizeValue(
			company[`bankProfileName${suffix}` as keyof CompanyBankProfiles]
		),
		bank: normalizeValue(
			company[`bank${suffix}` as keyof CompanyBankProfiles]
		),
		iban: normalizeValue(
			company[`iban${suffix}` as keyof CompanyBankProfiles]
		),
		bic: normalizeValue(
			company[`bic${suffix}` as keyof CompanyBankProfiles]
		),
		accountName: normalizeValue(
			company[`accountName${suffix}` as keyof CompanyBankProfiles]
		),
	}
}

export function getConfiguredBankAccountProfiles(
	company: CompanyBankProfiles | null | undefined
) {
	return BANK_PROFILE_IDS.map((profileId) =>
		getBankAccountProfile(company, profileId)
	).filter((profile) =>
		[profile.bank, profile.iban, profile.bic, profile.accountName].some(
			Boolean
		)
	)
}

export function getFirstConfiguredBankProfileId(
	company: CompanyBankProfiles | null | undefined
): BankProfileSelection {
	return getConfiguredBankAccountProfiles(company)[0]?.profileId ?? 'empty'
}

export function getBankProfileName(
	company: CompanyBankProfiles | null | undefined,
	profileId: BankProfileSelection | string | null | undefined
) {
	const profile = getBankAccountProfile(company, profileId)
	return profile.bankProfileName
}
